import "./global.css";

import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { createRoot } from "react-dom/client";
import type { Root } from "react-dom/client";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";

import { ThemeProvider } from "@/components/theme-provider";
import { SiteLayout } from "@/components/layout/SiteLayout";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import NotFound from "@/pages/NotFound";
import WordpressBaglanti from "@/pages/WordpressBaglanti";
import PdfOnizleme from "@/pages/PdfOnizleme";
import NumerolojiLanding from "@/pages/NumerolojiLanding";
import NumerolojiMagaza from "@/pages/NumerolojiMagaza";
import NumerolojiPaketDetay from "@/pages/NumerolojiPaketDetay";
import DigitalIcerikDetay from "@/pages/DigitalIcerikDetay";
import NumerolojiSepet from "@/pages/NumerolojiSepet";
import NumerolojiSatinAl from "@/pages/NumerolojiSatinAl";
import NumerolojiOdeme from "@/pages/NumerolojiOdeme";
import NumeralojiPremium from "@/pages/NumeralojiPremium";
import NumerolojiForm from "@/pages/NumerolojiForm";
import NumerolojiSuccess from "@/pages/NumerolojiSuccess";
import AdminNumeroloji from "@/pages/AdminNumeroloji";
import AdminNumerolojiManual from "@/pages/AdminNumerolojiManual";
import AdminNumerolojiDetail from "@/pages/AdminNumerolojiDetail";
import AdminNumerolojiProduct from "@/pages/AdminNumerolojiProduct";
import AdminNumerolojiPackage from "@/pages/AdminNumerolojiPackage";
import AdminNumerolojiMeditation from "@/pages/AdminNumerolojiMeditation";
import AdminNumerolojiProducts from "@/pages/AdminNumerolojiProducts";
import AdminNumerolojiPackages from "@/pages/AdminNumerolojiPackages";
import AdminNumerolojiMeditations from "@/pages/AdminNumerolojiMeditations";
import AdminNumerolojiBreaths from "@/pages/AdminNumerolojiBreaths";
import AdminNumerolojiBreath from "@/pages/AdminNumerolojiBreath";
import AdminNumerolojiDiets from "@/pages/AdminNumerolojiDiets";
import AdminNumerolojiDiet from "@/pages/AdminNumerolojiDiet";
import AdminNumerolojiActivities from "@/pages/AdminNumerolojiActivities";
import AdminNumerolojiActivity from "@/pages/AdminNumerolojiActivity";
import AdminNumerolojiReviews from "@/pages/AdminNumerolojiReviews";
import AdminNumerolojiOrders from "@/pages/AdminNumerolojiOrders";
import AdminNumerolojiOrderDetail from "@/pages/AdminNumerolojiOrderDetail";
import AdminNumerolojiSupport from "@/pages/AdminNumerolojiSupport";
import AdminNumerolojiReports from "@/pages/AdminNumerolojiReports";
import AdminNumerolojiBadges from "@/pages/AdminNumerolojiBadges";
import AdminNumerolojiFrekanslar from "@/pages/AdminNumerolojiFrekanslar";
import AdminNumerolojiOnboardingYorumlar from "@/pages/AdminNumerolojiOnboardingYorumlar";
import AdminNumerolojiAbonelikPlanlari from "@/pages/AdminNumerolojiAbonelikPlanlari";
import AdminNumerolojiKombinasyonlar from "@/pages/AdminNumerolojiKombinasyonlar";
import AdminNumerolojiIliskiAnalizi from "@/pages/AdminNumerolojiIliskiAnalizi";
import AdminMeditasyonMuzik from "@/pages/AdminMeditasyonMuzik";
import AdminKategoriler from "@/pages/AdminKategoriler";
import AdminUsers from "@/pages/AdminUsers";
import AdminContentPurchases from "@/pages/AdminContentPurchases";
import AdminIcerikSatinAlmalari from "@/pages/AdminIcerikSatinAlmalari";
import AdminSocialDashboard from "@/pages/AdminSocialDashboard";
import AdminSocialContentGenerator from "@/pages/AdminSocialContentGenerator";
import AdminSocialLunaSettings from "@/pages/AdminSocialLunaSettings";
import AdminSocialConnections from "@/pages/AdminSocialConnections";
import AdminSocialMediaManager from "@/pages/AdminSocialMediaManager";
import { AdminLayout } from "@/components/admin/AdminLayout";
import AdminBildirimler from "@/pages/AdminBildirimler";
import AdminSelmaTalepler from "@/pages/AdminSelmaTalepler";
import AdminSelmaRehber from "@/pages/AdminSelmaRehber";
import AdminSelmaDuygusalSebep from "@/pages/AdminSelmaDuygusalSebep";
// AdminSelmaFrekansSetleri: 2026-07-30'da menüden/route'tan bilinçli olarak askıya
// alındı (bkz. CLAUDE.md) — sayfa/route SİLİNMEDİ, sadece geçici olarak devre dışı.
// import AdminSelmaFrekansSetleri from "@/pages/AdminSelmaFrekansSetleri";
import AdminSelmaMeditasyonMuzikleri from "@/pages/AdminSelmaMeditasyonMuzikleri";
import AdminSelmaAyarlar from "@/pages/AdminSelmaAyarlar";
import KarmaHome from "@/pages/KarmaHome";
import KarmaMe from "@/pages/KarmaMe";
import KarmaCember from "@/pages/KarmaCember";
import KarmaProfile from "@/pages/KarmaProfile";
import Likes from "@/pages/Likes";
import Messages from "@/pages/Messages";
import { KarmaLayout } from "@/components/karma/KarmaLayout";
import { UserPanelLayout } from "@/components/panel/UserPanelLayout";
import UserPanelMagaza from "@/pages/UserPanelMagaza";
import UserPanelMagazaProduct from "@/pages/UserPanelMagazaProduct";
import UserPanelSiparislerim from "@/pages/UserPanelSiparislerim";
import UserPanelMeditasyon from "@/pages/UserPanelMeditasyon";
import UserPanelNefes from "@/pages/UserPanelNefes";
import UserPanelDiyet from "@/pages/UserPanelDiyet";
import UserPanelAktivite from "@/pages/UserPanelAktivite";
import UserPanelKesfet from "@/pages/UserPanelKesfet";
import UserPanelYakinimdakiRitueller from "@/pages/UserPanelYakinimdakiRitueller";
import UserPanelProfil from "@/pages/UserPanelProfil";
import UserPanelSosyalProfil from "@/pages/UserPanelSosyalProfil";
import UserPanelRituelSerisi from "@/pages/UserPanelRituelSerisi";
import UserPanelAkis from "@/pages/UserPanelAkis";
import UserPanelBildirimler from "@/pages/UserPanelBildirimler";
import UserPanelDestek from "@/pages/UserPanelDestek";
import UserPanelGunluk from "@/pages/UserPanelGunluk";
import UserPanelCemberler from "@/pages/UserPanelCemberler";
import UserPanelCemberDetay from "@/pages/UserPanelCemberDetay";
import UserPanelCemberYeni from "@/pages/UserPanelCemberYeni";
import UserPanelCemberGorevler from "@/pages/UserPanelCemberGorevler";

const queryClient = new QueryClient();

// In embedded preview/HMR environments, Supabase can log noisy warnings about missing refresh tokens
// while recovering sessions. They don't block functionality but clutter the console.
if (import.meta.env.DEV) {
  const origWarn = console.warn;
  console.warn = (...args: any[]) => {
    const first = args?.[0];
    if (typeof first === "string" && first.includes("No matching server or refresh token found")) {
      return;
    }
    return origWarn(...args);
  };

  const origError = console.error;
  console.error = (...args: any[]) => {
    const first = args?.[0];
    const msg =
      first instanceof Error
        ? `${first.name}: ${first.message}`
        : typeof first === "string"
          ? first
          : "";
    const lower = msg.toLowerCase();
    if (lower.includes("aborterror") && (lower.includes("signal is aborted") || lower.includes("aborted without reason"))) {
      return;
    }
    return origError(...args);
  };
}

// Supabase-js can sometimes emit AbortError rejections internally (especially in preview/HMR or when
// refresh checks are canceled). Those show up as noisy red errors but are not actionable for users.
// Prevent them from surfacing as unhandled promise rejections.
if (typeof window !== "undefined") {
  window.addEventListener("unhandledrejection", (event) => {
    const r = event.reason as any;
    const name = typeof r === "object" && r ? String(r?.name ?? "") : "";
    const msg =
      typeof r === "string"
        ? r
        : typeof r?.message === "string"
          ? r.message
          : "";

    const lower = String(msg).toLowerCase();

    if (name === "AbortError" || lower.includes("aborterror") || lower.includes("signal is aborted") || lower.includes("aborted")) {
      event.preventDefault();
    }
  });

  type MobilePlatform = "ios" | "android";
  type MobileBridgeVerifyPayload = {
    platform: MobilePlatform;
    storeProductId: string;
    transactionId?: string;
    originalTransactionId?: string;
    purchaseToken?: string;
    receiptData?: string;
    receipt?: string;
    verificationPayload?: unknown;
    orderId?: string;
    requestId?: string;
  };
  type MobileBridgeQueueItem =
    | { type: "verify"; payload: MobileBridgeVerifyPayload; requestedAt: number }
    | { type: "restore"; payload: { platform?: MobilePlatform; requestId?: string }; requestedAt: number };

  const queueKey = "__numerolojiMobileBridgeQueue";
  const listenerKey = "__numerolojiMobileBridgeMessageListenerAttached";
  const queue = ((window as any)[queueKey] ??= []) as MobileBridgeQueueItem[];

  const normalizePlatform = (value: unknown): MobilePlatform | null => {
    const raw = String(value ?? "").trim().toLowerCase();
    if (raw === "ios" || raw === "android") return raw;
    return null;
  };

  const createRequestId = () => {
    const fromCrypto = typeof crypto !== "undefined" && typeof crypto.randomUUID === "function" ? crypto.randomUUID() : "";
    return fromCrypto || `req_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
  };

  const emitBridgeResultToNative = (payload: { requestId: string; ok: boolean; result?: unknown; error?: string }) => {
    const serialized = JSON.stringify({
      type: "numeroloji.mobile.result",
      requestId: payload.requestId,
      ok: payload.ok,
      result: payload.result ?? null,
      error: payload.error ?? null,
    });

    try {
      (window as any)?.ReactNativeWebView?.postMessage?.(serialized);
    } catch {
      // ignore
    }

    try {
      window.parent?.postMessage?.(
        {
          type: "numeroloji.mobile.result",
          requestId: payload.requestId,
          ok: payload.ok,
          result: payload.result ?? null,
          error: payload.error ?? null,
        },
        "*",
      );
    } catch {
      // ignore
    }
  };

  const waitForBridgeResult = (requestId: string, successEventName: "numeroloji:mobile-payment-verified" | "numeroloji:mobile-payment-restore") => {
    return new Promise<any>((resolve, reject) => {
      const timeout = window.setTimeout(() => {
        window.removeEventListener(successEventName, onSuccess as EventListener);
        window.removeEventListener("numeroloji:mobile-payment-error", onError as EventListener);
        const message = "Mobil ödeme yanıtı zaman aşımına uğradı.";
        emitBridgeResultToNative({ requestId, ok: false, error: message });
        reject(new Error(message));
      }, 45_000);

      const cleanup = () => {
        window.clearTimeout(timeout);
        window.removeEventListener(successEventName, onSuccess as EventListener);
        window.removeEventListener("numeroloji:mobile-payment-error", onError as EventListener);
      };

      const onSuccess = (event: Event) => {
        const detail = (event as CustomEvent<any>)?.detail ?? {};
        const incomingRequestId = String(detail?.requestId ?? "").trim();
        if (!incomingRequestId || incomingRequestId !== requestId) return;
        cleanup();
        const result = detail?.result ?? null;
        emitBridgeResultToNative({ requestId, ok: true, result });
        resolve(result);
      };

      const onError = (event: Event) => {
        const detail = (event as CustomEvent<any>)?.detail ?? {};
        const incomingRequestId = String(detail?.requestId ?? "").trim();
        if (!incomingRequestId || incomingRequestId !== requestId) return;
        cleanup();
        const message = String(detail?.message ?? "Mobil ödeme işlemi başarısız.");
        emitBridgeResultToNative({ requestId, ok: false, error: message });
        reject(new Error(message));
      };

      window.addEventListener(successEventName, onSuccess as EventListener);
      window.addEventListener("numeroloji:mobile-payment-error", onError as EventListener);
    });
  };

  const requestVerify = (payload: MobileBridgeVerifyPayload) => {
    const platform = normalizePlatform(payload?.platform);
    const storeProductId = String(payload?.storeProductId ?? "").trim();
    if (!platform || !storeProductId) {
      return Promise.reject(new Error("platform ve storeProductId zorunlu."));
    }

    const requestId = String(payload?.requestId ?? "").trim() || createRequestId();
    const normalizedPayload: MobileBridgeVerifyPayload = {
      ...payload,
      platform,
      storeProductId,
      requestId,
    };

    const pending = waitForBridgeResult(requestId, "numeroloji:mobile-payment-verified");

    if (!(window as any).NumerolojiMobilePayments) {
      queue.push({ type: "verify", payload: normalizedPayload, requestedAt: Date.now() });
    }

    window.dispatchEvent(
      new CustomEvent("numeroloji:mobile-payment-verify-request", {
        detail: normalizedPayload,
      }),
    );

    return pending;
  };

  const requestRestore = (payload?: { platform?: MobilePlatform; requestId?: string }) => {
    const platform = payload?.platform ? normalizePlatform(payload.platform) : null;
    if (payload?.platform && !platform) {
      return Promise.reject(new Error("platform yalnızca ios veya android olabilir."));
    }

    const requestId = String(payload?.requestId ?? "").trim() || createRequestId();
    const normalizedPayload = platform ? { platform, requestId } : { requestId };
    const pending = waitForBridgeResult(requestId, "numeroloji:mobile-payment-restore");

    if (!(window as any).NumerolojiMobilePayments) {
      queue.push({ type: "restore", payload: normalizedPayload, requestedAt: Date.now() });
    }

    window.dispatchEvent(
      new CustomEvent("numeroloji:mobile-payment-restore-request", {
        detail: normalizedPayload,
      }),
    );

    return pending;
  };

  (window as any).NumerolojiMobileBridge = {
    verifyPurchase: requestVerify,
    restorePurchases: requestRestore,
  };

  if (!(window as any)[listenerKey]) {
    const onMessage = (event: MessageEvent) => {
      let data: any = event.data;
      if (typeof data === "string") {
        try {
          data = JSON.parse(data);
        } catch {
          return;
        }
      }
      if (!data || typeof data !== "object") return;

      const eventType = String((data as any).type ?? "").trim();
      if (eventType === "numeroloji.mobile.verify") {
        void requestVerify((data as any).payload ?? data).catch((error) => {
          const requestId = String((data as any)?.payload?.requestId ?? (data as any)?.requestId ?? "").trim();
          if (!requestId) return;
          emitBridgeResultToNative({
            requestId,
            ok: false,
            error: error instanceof Error ? error.message : "Mobil doğrulama isteği başarısız.",
          });
        });
      } else if (eventType === "numeroloji.mobile.restore") {
        void requestRestore((data as any).payload ?? data).catch((error) => {
          const requestId = String((data as any)?.payload?.requestId ?? (data as any)?.requestId ?? "").trim();
          if (!requestId) return;
          emitBridgeResultToNative({
            requestId,
            ok: false,
            error: error instanceof Error ? error.message : "Mobil geri yükleme isteği başarısız.",
          });
        });
      }
    };

    window.addEventListener("message", onMessage);
    (window as any)[listenerKey] = true;
  }
}

const App = () => (
  <QueryClientProvider client={queryClient}>
    <ThemeProvider>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <Routes>
            <Route element={<SiteLayout />}>
              <Route path="/" element={<Navigate to="/numeroloji" replace />} />
              <Route
                path="/wordpress-baglanti"
                element={<WordpressBaglanti />}
              />
              <Route path="/pdf-onizleme" element={<PdfOnizleme />} />

              <Route path="/numeroloji" element={<NumerolojiLanding />} />
              <Route path="/numeroloji/magaza" element={<NumerolojiMagaza />} />
              <Route path="/numeroloji/magaza/:key" element={<NumerolojiPaketDetay />} />
              <Route path="/numeroloji/dijital/:contentType/:id" element={<DigitalIcerikDetay />} />
              <Route path="/numeroloji/sepet" element={<NumerolojiSepet />} />
              <Route path="/numeroloji/satin-al" element={<NumerolojiSatinAl />} />
              <Route path="/numeroloji/premium" element={<NumeralojiPremium />} />
              <Route path="/numeroloji/odeme" element={<NumerolojiOdeme />} />
              <Route path="/numeroloji/form" element={<NumerolojiForm />} />
              <Route path="/numeroloji/basarili" element={<NumerolojiSuccess />} />
              <Route path="/numeroloji/admin" element={<Navigate to="/admin/numeroloji" replace />} />

              {/* Shorthand routes */}
              <Route path="/satin-al" element={<Navigate to="/numeroloji/satin-al" replace />} />
              <Route path="/sepet" element={<Navigate to="/numeroloji/sepet" replace />} />
              <Route path="/odeme" element={<Navigate to="/numeroloji/odeme" replace />} />
              <Route path="/form" element={<Navigate to="/numeroloji/form" replace />} />
              <Route path="/basarili" element={<Navigate to="/numeroloji/basarili" replace />} />

            </Route>

            <Route element={<AdminLayout />}>
              <Route path="/admin/numeroloji" element={<AdminNumeroloji />} />
              <Route path="/admin/numeroloji/yeni" element={<AdminNumerolojiManual />} />
              <Route path="/admin/numeroloji/urun" element={<AdminNumerolojiProducts />} />
              <Route path="/admin/numeroloji/urun/yeni" element={<AdminNumerolojiProduct />} />
              <Route path="/admin/numeroloji/urun/:id" element={<AdminNumerolojiProduct />} />
              <Route path="/admin/numeroloji/paket" element={<AdminNumerolojiPackages />} />
              <Route path="/admin/numeroloji/paket/yeni" element={<AdminNumerolojiPackage />} />
              <Route path="/admin/numeroloji/paket/:id" element={<AdminNumerolojiPackage />} />
              <Route path="/admin/numeroloji/meditasyon" element={<AdminNumerolojiMeditations />} />
              <Route path="/admin/numeroloji/meditasyon/yeni" element={<AdminNumerolojiMeditation />} />
              <Route path="/admin/numeroloji/meditasyon/:id" element={<AdminNumerolojiMeditation />} />
              <Route path="/admin/numeroloji/nefes" element={<AdminNumerolojiBreaths />} />
              <Route path="/admin/numeroloji/nefes/yeni" element={<AdminNumerolojiBreath />} />
              <Route path="/admin/numeroloji/nefes/:id" element={<AdminNumerolojiBreath />} />
              <Route path="/admin/numeroloji/diyet" element={<AdminNumerolojiDiets />} />
              <Route path="/admin/numeroloji/diyet/yeni" element={<AdminNumerolojiDiet />} />
              <Route path="/admin/numeroloji/diyet/:id" element={<AdminNumerolojiDiet />} />
              <Route path="/admin/numeroloji/aktivite" element={<AdminNumerolojiActivities />} />
              <Route path="/admin/numeroloji/aktivite/yeni" element={<AdminNumerolojiActivity />} />
              <Route path="/admin/numeroloji/aktivite/:id" element={<AdminNumerolojiActivity />} />
              <Route path="/admin/numeroloji/rituel" element={<AdminNumerolojiActivities />} />
              <Route path="/admin/numeroloji/rituel/yeni" element={<AdminNumerolojiActivity />} />
              <Route path="/admin/numeroloji/rituel/:id" element={<AdminNumerolojiActivity />} />
              <Route path="/admin/numeroloji/yorumlar" element={<AdminNumerolojiReviews />} />
              <Route path="/admin/numeroloji/siparisler" element={<AdminNumerolojiOrders />} />
              <Route path="/admin/numeroloji/siparisler/:id" element={<AdminNumerolojiOrderDetail />} />
              <Route path="/admin/numeroloji/destek" element={<AdminNumerolojiSupport />} />
              <Route path="/admin/numeroloji/sikayetler" element={<AdminNumerolojiReports />} />
              <Route path="/admin/numeroloji/rozetler" element={<AdminNumerolojiBadges />} />
              <Route path="/admin/bildirimler" element={<AdminBildirimler />} />
              <Route path="/admin/numeroloji/frekans" element={<AdminNumerolojiFrekanslar />} />
              <Route path="/admin/numeroloji/onboarding-yorumlar" element={<AdminNumerolojiOnboardingYorumlar />} />
              <Route path="/admin/numeroloji/abonelik-planlari" element={<AdminNumerolojiAbonelikPlanlari />} />
              <Route path="/admin/numeroloji/kombinasyonlar" element={<AdminNumerolojiKombinasyonlar />} />
              <Route path="/admin/numeroloji/iliski-analizi" element={<AdminNumerolojiIliskiAnalizi />} />
              <Route path="/admin/numeroloji/meditasyon-muzik" element={<AdminMeditasyonMuzik />} />
              <Route path="/admin/kategoriler" element={<AdminKategoriler />} />
              <Route path="/admin/users" element={<AdminUsers />} />
              <Route path="/admin/content-purchases" element={<AdminContentPurchases />} />
              <Route path="/admin/icerik-satin-almalari" element={<AdminIcerikSatinAlmalari />} />
              <Route path="/admin/social" element={<AdminSocialDashboard />} />
              <Route path="/admin/social/content-generator" element={<AdminSocialContentGenerator />} />
              <Route path="/admin/social/luna-settings" element={<AdminSocialLunaSettings />} />
              <Route path="/admin/social/connections" element={<AdminSocialConnections />} />
              <Route path="/admin/social/manager" element={<AdminSocialMediaManager />} />
              <Route path="/admin/numeroloji/:id" element={<AdminNumerolojiDetail />} />

              {/* DuyguŞifa "Selma" admin paneli — 2026-07-30'da duygusifa reposundan
                  taşındı. Yol öneki bilinçli olarak /admin değil /selma (numeroloji.app/selma). */}
              {/* Varsayılan yönlendirme 2026-07-31'de rehber'den talepler'e değişti —
                  Talepler artık sidebar'da en üstte, en sık kontrol edilecek operasyonel sayfa. */}
              <Route path="/selma" element={<Navigate to="/selma/talepler" replace />} />
              <Route path="/selma/talepler" element={<AdminSelmaTalepler />} />
              <Route path="/selma/rehber" element={<AdminSelmaRehber />} />
              <Route path="/selma/duygusal-sebep" element={<AdminSelmaDuygusalSebep />} />
              {/* /selma/frekans-setleri: 2026-07-30'da askıya alındı, bkz. CLAUDE.md.
                  Geri açmak için yukarıdaki import'u ve bu satırı yorumdan çıkar:
                  <Route path="/selma/frekans-setleri" element={<AdminSelmaFrekansSetleri />} /> */}
              <Route path="/selma/meditasyon-muzikleri" element={<AdminSelmaMeditasyonMuzikleri />} />
              <Route path="/selma/ayarlar" element={<AdminSelmaAyarlar />} />
            </Route>

            <Route path="/panel" element={<UserPanelLayout />}>
              <Route index element={<Navigate to="/panel/profil" replace />} />
              <Route path="cember" element={<UserPanelCemberler />} />
              <Route path="cember/yeni" element={<UserPanelCemberYeni />} />
              <Route path="cember/gorevler" element={<UserPanelCemberGorevler />} />
              <Route path="cember/:id" element={<UserPanelCemberDetay />} />
              <Route path="profil" element={<UserPanelSosyalProfil />} />
              <Route path="profil/rituel-serisi" element={<UserPanelRituelSerisi />} />
              <Route path="bildirimler" element={<UserPanelBildirimler />} />
              <Route path="akis" element={<UserPanelAkis />} />
              <Route path="ayarlar" element={<UserPanelProfil />} />
              <Route path="siparislerim" element={<UserPanelSiparislerim />} />
              <Route path="magaza" element={<UserPanelMagaza />} />
              <Route path="magaza/urun/:id" element={<UserPanelMagazaProduct />} />
              <Route path="meditasyon" element={<UserPanelMeditasyon />} />
              <Route path="nefes" element={<UserPanelNefes />} />
              <Route path="gunluk" element={<UserPanelGunluk />} />
              <Route path="diyet" element={<UserPanelDiyet />} />
              <Route path="aktivite" element={<UserPanelAktivite />} />
              <Route path="rituel" element={<UserPanelAktivite />} />
              <Route path="kesfet" element={<UserPanelKesfet />} />
              <Route path="kesfet/begeniler" element={<UserPanelKesfet />} />
              <Route path="kesfet/sohbet" element={<UserPanelKesfet />} />
              <Route path="kesfet/uyum/:targetUserId" element={<UserPanelKesfet />} />
              <Route path="kesfet/profil/:targetUserId" element={<UserPanelKesfet />} />
              <Route path="yakinimdaki-ritueller" element={<UserPanelYakinimdakiRitueller />} />
              <Route path="destek" element={<UserPanelDestek />} />
            </Route>

            <Route element={<KarmaLayout />}>
              <Route path="/karma" element={<KarmaHome />} />
              <Route path="/likes" element={<Likes />} />
              <Route path="/messages" element={<Messages />} />
              <Route path="/profile" element={<KarmaProfile />} />

              <Route path="/karma/me" element={<KarmaMe />} />
              <Route path="/karma/cember" element={<KarmaCember />} />
              <Route path="/karma/u/:handle" element={<KarmaProfile />} />
            </Route>

            <Route path="/rapor" element={<Navigate to="/numeroloji" replace />} />

            <Route path="*" element={<NotFound />} />
          </Routes>
        </BrowserRouter>
      </TooltipProvider>
    </ThemeProvider>
  </QueryClientProvider>
);

const container = document.getElementById("root");
if (!container) {
  throw new Error("Root container not found");
}

const ROOT_KEY = "__fusionReactRoot__";
let root = (globalThis as any)[ROOT_KEY] as Root | undefined;

if (!root) {
  root = createRoot(container);
  (globalThis as any)[ROOT_KEY] = root;
}

root.render(<App />);

if ((import.meta as any).hot) {
  (import.meta as any).hot.accept();
}
